import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { User } from '../types/alarm';
import { termsPrivacyService } from '../services/termsPrivacyService';
import Header from '../components/Header';
import { hideScrollbar } from '../styles/hideScrollbar';

interface TermsOfServicePageProps {
  user: User;
  onLogout: () => void;
  onNavigate: (page: 'home' | 'alarm' | 'admin' | 'assignments' | 'assignment-table' | 'assignment-card' | 'routine-detail' | 'points-table' | 'chat' | 'board' | 'license' | 'terms-of-service' | 'privacy-policy') => void;
  onUpdateUser: (updatedUser: User) => void;
  onProfileModalOpen: () => void;
  onClassManageModalOpen: () => void;
  classes: any[];
  currentClass: any | null;
  memberGroups?: string[];
  currentMemberGroup?: string;
  onMemberGroupChange?: (group: string) => void;
  themeMode: 'dark' | 'light';
  onToggleTheme: () => void;
  onSeatAssignmentClick?: () => void;
  onLevelSettingsClick?: () => void;
  onPointManagementClick?: () => void;
  onItemCardSettingsClick?: () => void;
  onAssignmentSettingsClick?: () => void;
  onLicenseClick?: () => void;
}

const TermsOfServicePage: React.FC<TermsOfServicePageProps> = ({
  user,
  onLogout,
  onNavigate,
  onUpdateUser,
  onProfileModalOpen,
  onClassManageModalOpen,
  classes,
  currentClass,
  memberGroups,
  currentMemberGroup,
  onMemberGroupChange,
  themeMode,
  onToggleTheme,
  onSeatAssignmentClick,
  onLevelSettingsClick,
  onPointManagementClick,
  onItemCardSettingsClick,
  onAssignmentSettingsClick,
  onLicenseClick,
}) => {
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    loadTermsOfUse();
  }, []);

  const loadTermsOfUse = async () => {
    setLoading(true);
    try {
      const data = await termsPrivacyService.getTermsOfUse();
      if (data && data.content) {
        setContent(data.content);
      } else {
        setContent('<p>서비스 이용약관 내용을 입력해주세요.</p>');
      }
    } catch (error) {
      console.error('서비스 이용약관 로드 실패:', error);
      setContent('<p>서비스 이용약관을 불러오는 데 실패했습니다.</p>');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Header
        user={user}
        onLogout={onLogout}
        onNavigate={onNavigate}
        currentPage="terms-of-service"
        onUpdateUser={onUpdateUser}
        onProfileModalOpen={onProfileModalOpen}
        onClassManageModalOpen={onClassManageModalOpen}
        classes={classes}
        currentClass={currentClass}
        memberGroups={memberGroups}
        currentMemberGroup={currentMemberGroup}
        onMemberGroupChange={onMemberGroupChange}
        themeMode={themeMode}
        onToggleTheme={onToggleTheme}
        onSeatAssignmentClick={onSeatAssignmentClick}
        onLevelSettingsClick={onLevelSettingsClick}
        onPointManagementClick={onPointManagementClick}
        onItemCardSettingsClick={onItemCardSettingsClick}
        onAssignmentSettingsClick={onAssignmentSettingsClick}
      />
      <Content>
        <ContentWrapper>
          <Title>서비스 이용약관</Title>
          <TermsContent>
            {loading ? (
              <LoadingText>로딩 중...</LoadingText>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: content }} />
            )}
          </TermsContent>
        </ContentWrapper>
        
        {/* 플랫폼 정보 섹션 */}
        <CompanyInfoSection>
          <CompanyInfoContent>
            <CompanyLogo>
              <img src="/LOGOeducodi_new.svg" alt="에듀코디" />
            </CompanyLogo>
            <CompanyInfoText>
              <div>제이앤조이 대표자 : 정영은</div>
              <div>통신판매업신고번호 : 2024-화성동탄-0316호</div>
              <div>사업자등록번호 : 702-04-02761</div>
              <div>주소 : 경기도 화성시 동탄대로시범길 20. 1415동 2701호</div>
              <PolicyLinks>
                <PolicyLink onClick={() => onNavigate('terms-of-service')}>[서비스 이용약관]</PolicyLink>
                <PolicyLink onClick={() => onNavigate('privacy-policy')}>[개인정보처리방침]</PolicyLink>
              </PolicyLinks>
              <div>연락처 : 010-7442-0199</div>
              <div>결제 및 상담 : luckytem_mk@naver.com</div>
            </CompanyInfoText>
          </CompanyInfoContent>
        </CompanyInfoSection>
      </Content>
    </Container>
  );
};

export default TermsOfServicePage;

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-bg);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Content = styled.div`
  position: absolute;
  top: 44px;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: none;
  -webkit-overflow-scrolling: touch;
  z-index: 1;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  ${hideScrollbar}
`;

const ContentWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  margin: 40px 0 32px 0;
  text-align: center;
  
  /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
  :root[data-theme='light'] & {
    color: #000000;
  }
  
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const TermsContent = styled.div`
  background: transparent;
  border-radius: 12px;
  padding: 32px;
  line-height: 1.8;
  color: var(--color-text-primary);

  /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
  :root[data-theme='light'] & {
    color: #000000;
  }

  :root:not([data-theme='light']) & {
    color: #ffffff;
  }

  /* HTML 콘텐츠에도 다크모드 스타일 적용 */
  :root:not([data-theme='light']) & * {
    color: #ffffff !important;
  }

  p {
    margin: 0 0 16px 0;

    /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
    :root[data-theme='light'] & {
      color: #000000;
    }

    :root:not([data-theme='light']) & {
      color: #ffffff;
    }
  }

  h2 {
    font-size: 20px;
    font-weight: 600;
    margin: 32px 0 16px 0;

    /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
    :root[data-theme='light'] & {
      color: #000000;
    }

    :root:not([data-theme='light']) & {
      color: #ffffff;
    }
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 24px 0 12px 0;

    /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
    :root[data-theme='light'] & {
      color: #000000;
    }

    :root:not([data-theme='light']) & {
      color: #ffffff;
    }
  }

  ul, ol {
    margin: 16px 0;
    padding-left: 24px;

    /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
    :root[data-theme='light'] & {
      color: #000000;
    }

    :root:not([data-theme='light']) & {
      color: #ffffff;
    }
  }

  li {
    margin: 8px 0;

    /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
    :root[data-theme='light'] & {
      color: #000000;
    }

    :root:not([data-theme='light']) & {
      color: #ffffff;
    }
  }

  /* 추가: 모든 HTML 요소에 다크모드 색상 적용 */
  :root:not([data-theme='light']) & p,
  :root:not([data-theme='light']) & h1,
  :root:not([data-theme='light']) & h2,
  :root:not([data-theme='light']) & h3,
  :root:not([data-theme='light']) & h4,
  :root:not([data-theme='light']) & h5,
  :root:not([data-theme='light']) & h6,
  :root:not([data-theme='light']) & span,
  :root:not([data-theme='light']) & div,
  :root:not([data-theme='light']) & strong,
  :root:not([data-theme='light']) & em,
  :root:not([data-theme='light']) & li {
    color: #ffffff !important;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  
  /* 라이트 모드에서는 검정색, 다크 모드에서는 흰색 */
  :root[data-theme='light'] & {
    color: #000000;
  }
  
  :root:not([data-theme='light']) & {
    color: #ffffff;
  }
`;

const CompanyInfoSection = styled.div`
  margin-top: 60px;
  padding: 40px 20px 100px 20px;
  background: var(--color-surface);
  border-top: 1px solid var(--color-border);
  width: 100%;
  box-sizing: border-box;
  flex-shrink: 0;
  
  /* 라이트 모드에서 메인테마 초록색 배경 */
  :root[data-theme='light'] & {
    background: var(--color-accent-strong);
  }
`;

const CompanyInfoContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 0 20px;
  box-sizing: border-box;
`;

const CompanyLogo = styled.div`
  flex-shrink: 0;
  
  img {
    height: 20px;
    width: auto;
    
    @media (max-width: 768px) {
      height: 18px;
    }
  }
`;

const CompanyInfoText = styled.div`
  font-size: 11px;
  line-height: 1.5;
  color: var(--color-text-secondary);
  opacity: 0.7;
  text-align: center;
  width: 100%;
  
  /* 라이트 모드에서 텍스트를 흰색으로 */
  :root[data-theme='light'] & {
    color: rgba(255, 255, 255, 0.9);
    opacity: 1;
  }
  
  div {
    margin-bottom: 2px;
  }
  
  div:last-child {
    margin-bottom: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const PolicyLinks = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin: 6px 0;
  
  @media (max-width: 768px) {
    gap: 6px;
    margin: 4px 0;
  }
`;

const PolicyLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 11px;
  color: var(--color-text-secondary);
  text-decoration: underline;
  opacity: 0.7;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: var(--color-accent-text);
  }

  :root[data-theme='light'] & {
    color: rgba(255, 255, 255, 0.9);
    opacity: 1;
  }

  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

